import { all, call, put, select, spawn, takeLatest } from 'redux-saga/effects';

import {
  AnalyticsEventNames,
  Categories,
  DataLayerEventName,
  pushToAdobeDataLayer,
} from '@app/services/adobeDataLayer';
import i18n from '@app/services/translations';

import { GetTransactionIdInput, Locale, MakePaymentInput, Query } from '@mfe/shared/schema-types';
import { getAutopayPreauthAmount } from '@mfe/shared/utils';

import { Alert, removeAlert, selectAlerts } from '../alerts';
import { selectUser } from '../auth';
import { refetchBillingInfo, selectBillingInfo } from '../billingInfo';
import { selectConfig } from '../config';
import { FetchWithErrorsMutation, graphqlMutationWithErrors, graphqlQuery } from '../graphql';
import { selectLocale } from '../locale';
import { selectUserInfo } from '../userInfo';
import {
  initiatePayment,
  modifyTransaction as modifyTransactionAction,
  ModifyTransactionAction,
  PaymentAction,
  PaymentMethodType,
  retrieveTransactionId,
  selectPayments,
  setCurrentTxnId,
  setPaymentLoading,
  setPayments,
  setTransactionIdError,
} from './paymentsSlice';
import { GET_TRANSACTION_ID, MAKE_PAYMENT, MODIFY_TRANSACTION } from './queries-and-mutations';
import { saveAlertNameInStorage } from '../../utils';
import { TxnType } from '../../../../platform/src/server/dataSources/VPP/types';

export function* modifyTransaction(action: ModifyTransactionAction) {
  const { amount } = action.payload;
  const { currentTxnId } = yield select(selectPayments);
  const { vppUrl }: ReturnType<typeof selectConfig> = yield select(selectConfig);
  const {
    userInfo: { accountNumber },
  }: ReturnType<typeof selectUserInfo> = yield select(selectUserInfo);

  yield put(setPayments({ frameReady: false }));
  const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: MODIFY_TRANSACTION,
    variables: {
      input: {
        transactionId: currentTxnId,
        userAgent: window.navigator.userAgent,
        txnType: TxnType.PREAUTHORIZE,
        txnAmount: amount,
        paymentSource: 'MyViasat',
        additionalDetails: { billingAccount: accountNumber },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const { errors, runtimeError } = response;

  if (runtimeError || errors) {
    const modifyError =
      errors && typeof errors === 'object'
        ? (errors as Record<string, { code?: string; message?: string } | undefined>)['modifyTransaction']
        : undefined;

    yield put(
      setPayments({
        loading: false,
        error: modifyError ? { code: modifyError.code, message: modifyError.message } : runtimeError ?? errors,
      })
    );
    return;
  }

  const url = currentTxnId ? `${vppUrl}?id=${currentTxnId}` : '';
  yield put(setPayments({ url, frameReady: true }));
}

export function* getTransactionId({ payload }: PaymentAction) {
  const {
    locale: { userLocale },
  }: ReturnType<typeof selectLocale> = yield select(selectLocale);

  const {
    billingInfo: { billingAccount },
  }: ReturnType<typeof selectBillingInfo> = yield select(selectBillingInfo);

  const {
    userInfo: { phone },
  }: ReturnType<typeof selectUserInfo> = yield select(selectUserInfo);

  const { vppUrl, isPreauthZeroEnabled }: ReturnType<typeof selectConfig> = yield select(selectConfig);

  const { txnAmount, paymentMethodType }: ReturnType<typeof selectPayments> = yield select(selectPayments);

  const txnValue = () => {
    const preauthAmount = getAutopayPreauthAmount(isPreauthZeroEnabled);

    if (userLocale === Locale.PtBr) {
      return payload?.amountOwedOnInvoice?.value;
    }

    if (paymentMethodType === PaymentMethodType.CARD && txnAmount === 0 && !payload?.invoiceNumber) {
      return preauthAmount;
    }

    return txnAmount;
  };

  const input: GetTransactionIdInput = {
    userAgent: window.navigator.userAgent,
    txnAmount: {
      alphabeticCode: billingAccount?.balance?.alphabeticCode ?? 'USD',
      value: txnValue(),
    },
    phone: phone.primary?.phoneNumber,
    invoiceNumber: payload?.invoiceNumber,
    transactionType: payload?.invoiceNumber ? 'OTP' : undefined,
  };

  try {
    yield put(setPaymentLoading(true));
    yield put(setPayments({ frameReady: false }));
    const data: Query = yield call(graphqlQuery, {
      query: GET_TRANSACTION_ID,
      variables: { input },
      fetchPolicy: 'no-cache',
    });

    const transactionId = data?.getTransactionId?.transactionId;
    yield put(setCurrentTxnId(transactionId));
    const url = transactionId ? `${vppUrl}?id=${transactionId}` : '';
    yield put(setPayments({ url }));
  } catch (error) {
    const getTransactionIdErrorCode = '[getTransactionId] Failed to fetch';
    const getTransactionIdErrorMessage = 'Failed to fetch transaction ID';
    const payload = {
      error: {
        code: getTransactionIdErrorCode,
        message: getTransactionIdErrorMessage,
      },
    };

    yield put(setTransactionIdError(payload));
    yield put(setPaymentLoading(false));
  }
}

export function* makePayment({ payload }: PaymentAction) {
  const {
    billingInfo: { billingAccount, autoPay },
  }: ReturnType<typeof selectBillingInfo> = yield select(selectBillingInfo);

  const {
    userInfo: { phone },
  }: ReturnType<typeof selectUserInfo> = yield select(selectUserInfo);

  const { txnAmount }: ReturnType<typeof selectPayments> = yield select(selectPayments);

  const input: MakePaymentInput = {
    userAgent: window.navigator.userAgent,
    txnAmount: {
      alphabeticCode: billingAccount?.balance?.alphabeticCode ?? 'USD',
      value: txnAmount,
    },
    phone: phone.primary?.phoneNumber,
    invoiceNumber: payload?.invoiceNumber,
  };

  const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: MAKE_PAYMENT,
    variables: { input },
    fetchPolicy: 'no-cache',
  });

  const { data, runtimeError, errors } = response;

  if (runtimeError || errors) {
    yield put(setPayments({ error: runtimeError ?? errors, submit: false }));
    return;
  }

  const paymentDetails = {
    payment_type: 'recurring',
    payment_amount_type: billingAccount?.balance?.value === txnAmount ? 'full' : 'partial',
    agent_fee: 'not_applicable',
    payment_value: txnAmount,
    payment_currency: billingAccount?.balance?.alphabeticCode ?? '',
    payment_method_type: autoPay?.paymentMethod,
  };

  pushToAdobeDataLayer({
    event: DataLayerEventName.FORM_START,
    event_name: DataLayerEventName.FORM_START,
    event_label: `${AnalyticsEventNames.OneTimePaymentStarted} | PaymentMethodType: ${paymentDetails.payment_method_type}`,
    event_category: Categories.Billing,
  });

  if (data?.makePayment?.success) {
    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_SUBMIT,
      event_name: DataLayerEventName.FORM_SUBMIT,
      event_label: `${AnalyticsEventNames.OneTimePaymentCompleted} | PaymentMethodType: ${paymentDetails.payment_method_type} | PaymentAmountType: ${paymentDetails.payment_amount_type} | PaymentValue: ${paymentDetails.payment_value} | PaymentCurrency: ${paymentDetails.payment_currency} `,
      event_category: Categories.Billing,
    });

    yield put(setPayments({ success: true, paymentMethod: autoPay ?? undefined }));

    const alertToBeRemoved: Alert | undefined = yield call(getAlertToRemove);
    if (!alertToBeRemoved) return;
    yield put(removeAlert(alertToBeRemoved.type));
    yield call(saveAlertNameInStorage, alertToBeRemoved.type);

    yield put(refetchBillingInfo());
  } else {
    const error = data?.makePayment?.error ?? undefined;

    const paymentDetailsFailure = {
      ...paymentDetails,
      failure_reason: error?.message ?? 'Payment Failed.',
    };

    pushToAdobeDataLayer({
      event: DataLayerEventName.FORM_SUBMIT,
      event_name: DataLayerEventName.FORM_SUBMIT,
      event_label: `${AnalyticsEventNames.OneTimePaymentFailed} | PaymentMethodType: ${paymentDetailsFailure.payment_method_type} | PaymentAmountType: ${paymentDetailsFailure.payment_amount_type} | PaymentValue: ${paymentDetailsFailure.payment_value} | PaymentCurrency: ${paymentDetailsFailure.payment_currency} | FailureReason: ${paymentDetailsFailure.failure_reason}`,
      event_category: Categories.Billing,
    });

    yield put(setPayments({ error, submit: false }));
  }
}

function* getAlertToRemove() {
  const { alerts }: ReturnType<typeof selectAlerts> = yield select(selectAlerts);

  const suspensionType = 'suspended';

  const alertTitles = [
    i18n.t(`Alerts:AccountStatus.${suspensionType}.title`),
    i18n.t(`Alerts:AccountStatus.disconnected.title`),
    i18n.t('Alerts:AccountStatus.pastDue.title'),
    i18n.t('Alerts:AccountStatus.softSuspend.title'),
    i18n.t('Alerts:AccountStatus.preSoftSuspend.title'),
  ];

  const alertToBeRemoved = alerts.find((alert) => alertTitles.includes(alert.title || ''));

  if (!alertToBeRemoved) {
    return;
  }

  return alertToBeRemoved;
}

export function* watchPayments() {
  yield spawn(function* () {
    yield all([
      takeLatest(retrieveTransactionId.type, getTransactionId),
      takeLatest(modifyTransactionAction.type, modifyTransaction),
      takeLatest(initiatePayment.type, makePayment),
    ]);
  });
}
